import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { FormActions, FormDialog, FormField, RelativeDatePicker } from "@/components/forms";
import { formStyles } from "@/components/forms/formStyles";
import { parseDateTimeOrNull } from "@/lib/date-utils";

type PricePointDraft = {
  label: string;
  description: string;
  priceCents: number;
  currency: string;
  sortOrder: number;
  availableFrom: Date | null;
  availableUntil: Date | null;
  depositCents: number | null;
  balanceDueAt: Date | null;
  /** null = unlimited */
  capacity: number | null;
};

export function PricePointDialog(props: {
  visible: boolean;
  editing: boolean;
  value: PricePointDraft;
  onChange: (next: PricePointDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  eventStartsAt: string | null | undefined;
  eventBookingClosesAt: string | null | undefined;
}) {
  const { value } = props;

  return (
    <FormDialog
      header={props.editing ? "Edit Price Point" : "Add Price Point"}
      visible={props.visible}
      onHide={props.onCancel}
    >
      <FormField id="pp-label" label="Label" required>
        <InputText
          id="pp-label"
          value={value.label}
          onChange={(e) => props.onChange({ ...value, label: e.target.value })}
          className="w-full"
          style={formStyles.input}
        />
      </FormField>

      <FormField id="pp-description" label="Description" optional>
        <InputTextarea
          id="pp-description"
          value={value.description}
          onChange={(e) =>
            props.onChange({
              ...value,
              description: e.target.value,
            })
          }
          rows={3}
          className="w-full"
          style={formStyles.input}
        />
      </FormField>

      <FormField id="pp-price" label="Price" required>
        <InputNumber
          id="pp-price"
          value={value.priceCents / 100}
          onValueChange={(e) =>
            props.onChange({
              ...value,
              priceCents: Math.round((e.value || 0) * 100),
            })
          }
          mode="decimal"
          min={0}
          minFractionDigits={2}
          maxFractionDigits={2}
          prefix="£"
          className="w-full"
          inputStyle={formStyles.input}
        />
      </FormField>

      <FormField
        id="pp-capacity"
        label="Capacity"
        optional
        helperText="Maximum bookings for this category (pending, approved, and incomplete checkouts). Leave empty for unlimited."
      >
        <InputNumber
          id="pp-capacity"
          value={value.capacity ?? undefined}
          onValueChange={(e) =>
            props.onChange({
              ...value,
              capacity:
                e.value === null || e.value === undefined
                  ? null
                  : Math.max(1, Math.floor(e.value)),
            })
          }
          min={1}
          showButtons
          placeholder="Unlimited"
          className="w-full"
          inputStyle={formStyles.input}
        />
      </FormField>

      <FormField
        id="pp-deposit"
        label="Deposit"
        optional
        helperText="Amount to charge on booking approval (must be at least £5 less than total price)"
      >
        <InputNumber
          id="pp-deposit"
          value={
            (value.depositCents ?? null) !== null ? (value.depositCents || 0) / 100 : null
          }
          onValueChange={(e) => {
            const depositCents = e.value !== null ? Math.round((e.value || 0) * 100) : null;
            const next: PricePointDraft = {
              ...value,
              depositCents,
            };
            if (!depositCents) {
              next.balanceDueAt = null;
            }
            props.onChange(next);
          }}
          mode="decimal"
          min={0}
          minFractionDigits={2}
          maxFractionDigits={2}
          prefix="£"
          placeholder="No deposit"
          className="w-full"
          inputStyle={formStyles.input}
        />
      </FormField>

      {value.depositCents ? (
        <RelativeDatePicker
          id="pp-balance-due-at"
          label="Balance Due At"
          optional
          value={value.balanceDueAt}
          onChange={(date) => props.onChange({ ...value, balanceDueAt: date })}
          referenceDate={parseDateTimeOrNull(props.eventStartsAt)}
          referenceDateLabel="event start"
          helperText="When the remaining balance should be collected"
        />
      ) : null}

      <RelativeDatePicker
        id="pp-available-from"
        label="Available From"
        optional
        value={value.availableFrom}
        onChange={(date) => props.onChange({ ...value, availableFrom: date })}
        referenceDate={parseDateTimeOrNull(props.eventStartsAt)}
        referenceDateLabel="event start"
      />

      <RelativeDatePicker
        id="pp-available-until"
        label="Available Until"
        optional
        value={value.availableUntil}
        onChange={(date) => props.onChange({ ...value, availableUntil: date })}
        referenceDate={
          props.eventBookingClosesAt
            ? new Date(props.eventBookingClosesAt)
            : parseDateTimeOrNull(props.eventStartsAt)
        }
        referenceDateLabel={props.eventBookingClosesAt ? "booking close" : "event start"}
      />

      <FormActions
        primaryLabel="Save"
        onPrimary={props.onSave}
        secondaryLabel="Cancel"
        onSecondary={props.onCancel}
      />
    </FormDialog>
  );
}


